import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";
import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import { INVENTORY_CATEGORIES } from "./types";
import { categoryApi } from "./apiService";
import { useToast } from "../../../components/Toast";

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  existingCategories?: string[];
  fetchCategories: () => void;
}

const AddCategoryModal = ({
  open,
  onClose,
  existingCategories = [],
  fetchCategories,
}: AddCategoryModalProps) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationSchema = Yup.object().shape({
    categoryName: Yup.string()
      .trim()
      .min(2, "Category must be at least 2 characters")
      .required("Category is required")
      .test("unique", "Category already exists", (value) => {
        if (!value) return true;
        const v = value.trim().toLowerCase();
        return !existingCategories.some(
          (c) => (c || "").trim().toLowerCase() === v
        );
      }),
    description: Yup.string().max(120, "Max 120 characters").optional(),
  });

  const initialValues = {
    categoryName: "",
    description: "",
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setIsSubmitting(true);
      await categoryApi.addCategory(values);
      onClose();
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      showToast("Something went wrong! Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", mb: 0.5, pb: 1 }}>
        Add Inventory Category
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={INVENTORY_CATEGORIES}
                    value={values.categoryName}
                    onChange={(event, value) => {
                      setFieldValue("categoryName", value);
                    }}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") {
                        setFieldValue("categoryName", value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category Name"
                        {...commonTextFieldProps}
                        autoFocus
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} style={{ fontSize: "0.75rem" }}>
                        {option}
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="description"
                    label="Description (optional)"
                    fullWidth
                    multiline
                    rows={2}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={onClose}
                {...smallButtonProp}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                {...smallButtonProp}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddCategoryModal;
